/**
 * Feature flags and subscription tier definitions
 *
 * This module defines the premium features and usage limits for different
 * subscription tiers. Used throughout the app for feature gating.
 */

// Premium feature identifiers
export const PREMIUM_FEATURES = {
  SMART_REMINDERS: 'smart_reminders',
  LAWN_HEALTH_TRACKING: 'lawn_health_tracking',
  UNLIMITED_ANALYSES: 'unlimited_analyses',
  HISTORICAL_DATA: 'historical_data',
  SOIL_TEST_PERSISTENCE: 'soil_test_persistence',
} as const

export type PremiumFeature = typeof PREMIUM_FEATURES[keyof typeof PREMIUM_FEATURES]

// Usage limits for free tier
export const FREE_LIMITS = {
  ANALYSES_PER_MONTH: 3,
  CARE_PLANS_PER_MONTH: 1,
  CHAT_MESSAGES_PER_MONTH: 10,
  // Free is already bound by the 10/month ceiling, so the daily cap is a no-op
  // for this tier — kept here only so FREE_LIMITS and PREMIUM_LIMITS stay
  // key-symmetric (they're used interchangeably via `typeof`).
  CHAT_MESSAGES_PER_DAY: Infinity,
  SAVED_LAWNS: Infinity,
  HISTORY_DAYS: 30,
  // 2 per IP per month: enough for front + back yard or one retry after a
  // failed parse. Only successful parses count (failed parses + manual
  // entries are free; see lib/auth/usage-tracking.ts attemptAnonymousSoilTestParse).
  SOIL_TEST_PARSES_PER_MONTH: 2,
} as const

// NOTE: `weed` shares the DIAGNOSE bucket by design — see the featureForMode
// note in lib/auth/usage-tracking.ts. `getAnonymousWebAnalysisLimit` therefore
// never reads `.weed` here; it falls through to `.diagnose`. The key is kept so
// `LIMITS[mode]` type-checks for every AnalyzeMode, and it is deliberately set
// equal to `diagnose` so a stray read cannot silently hand out a second free
// scan. Changing this value alone does NOT give weed its own allowance.
export const ANONYMOUS_WEB_ANALYSIS_LIMITS = {
  identify: 1,
  diagnose: 1,
  weed: 1,
} as const

/**
 * Allowance for a browser that has given us an email but has NOT created an
 * account (EMAIL_UNLOCK_COOKIE present — see lib/email-unlock-cookie.ts).
 *
 * Sits between the no-account allowance and the registered-free allowance so
 * the ladder is anonymous -> email -> account -> premium, and every rung is
 * paid for with something durable. Measured appetite is ~1.8 scans in an active
 * month, so 3 covers the large majority of real usage without reaching the
 * registered tier.
 *
 * Same weed-shares-diagnose invariant as the objects above and below: `weed`
 * is deliberately equal to `diagnose` because featureForMode() maps them onto
 * one counter. Changing it alone does NOT grant weed its own allowance, it just
 * makes a stray read hand out a scan the counter never reserved.
 */
export const EMAIL_TIER_ANALYSIS_LIMITS = {
  identify: 3,
  diagnose: 3,
  weed: 3,
} as const

export const REGISTERED_FREE_ANALYSIS_LIMITS = {
  identify: FREE_LIMITS.ANALYSES_PER_MONTH,
  diagnose: FREE_LIMITS.ANALYSES_PER_MONTH,
  weed: FREE_LIMITS.ANALYSES_PER_MONTH,
} as const

export function formatLimit(n: number): string {
  return Number.isFinite(n) ? String(n) : 'Unlimited'
}

// JSON.stringify(Infinity) is null. Use -1 as the wire sentinel for
// unlimited limits; clients pass through isUnlimited() at consumer layer.
export const UNLIMITED_SENTINEL = -1
export function serializeLimit(n: number): number {
  return Number.isFinite(n) ? n : UNLIMITED_SENTINEL
}
export function isUnlimited(n: number): boolean {
  return n === UNLIMITED_SENTINEL || !Number.isFinite(n)
}

/**
 * True when a usage meter has reached `threshold` (default 50%) of its limit.
 * Unlimited limits (Infinity or the -1 wire sentinel) and non-positive limits
 * never warn. Pure + exported so the dashboard FreeTierCard rule is unit-testable.
 */
export function meterIsNearLimit(used: number, limit: number, threshold = 0.5): boolean {
  if (isUnlimited(limit) || limit <= 0) return false
  return used / limit >= threshold
}

// AI-cost safety caps for the assistant chat (the "Neighbor Pro" persona runs
// gpt-5.1 through the agent loop). Premium is NOT unlimited: a high but finite
// ceiling stops a shared/leaked/scripted account from burning AI credits while
// staying far above any legitimate power user (~30/day on a heavy day). Tunable
// via env without a code change — mirrors CHAT_MAX_TOOL_ROUNDS in the loop.
const PREMIUM_CHAT_PER_DAY = parseInt(process.env.CHAT_PREMIUM_DAILY_CAP || '100', 10)
const PREMIUM_CHAT_PER_MONTH = parseInt(process.env.CHAT_PREMIUM_MONTHLY_CAP || '2000', 10)

// Usage limits for premium tier
export const PREMIUM_LIMITS = {
  ANALYSES_PER_MONTH: Infinity,
  CARE_PLANS_PER_MONTH: Infinity,
  CHAT_MESSAGES_PER_MONTH: PREMIUM_CHAT_PER_MONTH,
  CHAT_MESSAGES_PER_DAY: PREMIUM_CHAT_PER_DAY,
  SAVED_LAWNS: Infinity,
  HISTORY_DAYS: Infinity,
  SOIL_TEST_PARSES_PER_MONTH: Infinity,
} as const

// Subscription status types
export type SubscriptionStatus = 'free' | 'premium' | 'cancelled' | 'past_due'
export type SubscriptionTier = 'free' | 'monthly' | 'yearly'

export const PREMIUM_TRIAL_DAYS = 14

/**
 * Standing discount applied to every Stripe (web) checkout, so buying direct
 * costs less than buying through the App Store. The Stripe coupon named by
 * `STRIPE_WEB_DISCOUNT_COUPON_ID` is what actually gets charged; this constant
 * exists so pricing surfaces can render the same number without a Stripe
 * round-trip. The two must agree — if they drift, the page quotes one price and
 * the card is charged another.
 *
 * Set to 0 to turn the offer off everywhere (display and charge) in one move.
 */
export const WEB_DISCOUNT_PERCENT = 10

/**
 * Mirrors how Stripe applies `percent_off` to an invoice subtotal: exact
 * percentage, rounded to the nearest cent.
 */
export function applyWebDiscount(amountInCents: number): number {
  return Math.round((amountInCents * (100 - WEB_DISCOUNT_PERCENT)) / 100)
}

// Pricing configuration (in cents for Stripe)
export const PRICING = {
  MONTHLY: {
    amount: 799, // $7.99
    currency: 'usd',
    interval: 'month' as const,
    lookup_key: 'monthly',
    display: '$7.99/month',
  },
  YEARLY: {
    amount: 4799, // $47.99
    currency: 'usd',
    interval: 'year' as const,
    lookup_key: 'yearly',
    display: '$47.99/year',
    savings: '$47.89', // vs monthly
  },
} as const

// Feature descriptions for UI
export const FEATURE_DESCRIPTIONS: Record<PremiumFeature, {
  name: string
  description: string
  icon: string
}> = {
  [PREMIUM_FEATURES.SMART_REMINDERS]: {
    name: 'Smart Reminders',
    description: 'Get email and push notifications for lawn care tasks based on your grass type and local weather',
    icon: 'bell',
  },
  [PREMIUM_FEATURES.LAWN_HEALTH_TRACKING]: {
    name: 'Lawn Health Tracking',
    description: 'Upload photos over time and track your lawn\'s improvement with AI-powered analysis',
    icon: 'trending-up',
  },
  [PREMIUM_FEATURES.UNLIMITED_ANALYSES]: {
    name: 'Unlimited Analyses',
    description: 'No monthly limits on grass identification and lawn analysis',
    icon: 'infinity',
  },
  [PREMIUM_FEATURES.HISTORICAL_DATA]: {
    name: 'Historical Data',
    description: 'Access all your past analyses and track what treatments worked for your lawn',
    icon: 'history',
  },
  [PREMIUM_FEATURES.SOIL_TEST_PERSISTENCE]: {
    name: 'Saved Soil Tests',
    description: 'Save soil test analyses to your lawn profile, track changes year over year, and get 12-month re-test reminders',
    icon: 'flask-conical',
  },
}

/**
 * Check if a feature requires premium subscription
 */
export function isPremiumFeature(feature: string): boolean {
  return Object.values(PREMIUM_FEATURES).includes(feature as PremiumFeature)
}

/**
 * Get the appropriate limits based on subscription status
 */
export function getLimitsForTier(isPremium: boolean): typeof FREE_LIMITS | typeof PREMIUM_LIMITS {
  return isPremium ? PREMIUM_LIMITS : FREE_LIMITS
}

/**
 * Check if user has access to a specific feature
 */
export function hasFeatureAccess(
  subscriptionStatus: SubscriptionStatus,
  feature: PremiumFeature
): boolean {
  // Free features available to everyone
  const freeFeatures: PremiumFeature[] = []

  if (freeFeatures.includes(feature)) {
    return true
  }

  // Premium features require active subscription
  return subscriptionStatus === 'premium'
}

/**
 * Get remaining usage for a feature based on current usage and tier
 */
export function getRemainingUsage(
  currentUsage: number,
  isPremium: boolean,
  limitType: keyof typeof FREE_LIMITS
): number {
  const limits = getLimitsForTier(isPremium)
  const limit = limits[limitType]

  if (limit === Infinity) {
    return Infinity
  }

  return Math.max(0, limit - currentUsage)
}
